import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  FieldArray,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  AttractionsImageUpload,
  createAttractions,
  getAllActiveAttractions,
  getAttractionsByID,
  updateAttractions,
} from "./services/attractions.services";
import { clearAttractionsDetails } from "./attractionsSlice";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import { getFile, slugify } from "../../_helper/helper";
import WhyVisitArray from "./WhyVisitArray";
import HowToReachArray from "./HowToReachArray";
import BestTimeToVisitArray from "./BestTimeToVisitArray";
import ThingsToDoThereArray from "./ThingsToDoThereArray";
import AttractionNearbyArray from "./AttractionNearbyArray";
import FormTextArea from "../../components/formFields/FormTextArea";
import PhotoGalleryArray from "./PhotoGalleryArray";
import FormCheckbox from "../../components/formFields/FormCheckbox";
import { IProperty } from "../property/propertyModel";
import { getAllActiveProperties } from "../property/services/property.services";

const CreateAttractions: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const AttractionsID = location.state?.AttractionsID;
  const AttractionsDetail = useAppSelector(
    (state) => state.attractions.AttractionsDetail,
  );
  const PropertyList = useAppSelector((state) => state.property.PropertyList);
  const loading = useAppSelector((state) => state.attractions.loading);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [AttractionsDetail]);

  useEffect(() => {
    dispatch(getAllActiveProperties());
    dispatch(getAllActiveAttractions());
    if (AttractionsID) {
      dispatch(getAttractionsByID(AttractionsID));
    }
    return () => {
      dispatch(clearAttractionsDetails());
    };
  }, [AttractionsID]);

  const handleSubmit = async (values: any) => {
    const insertPayload = {
      Title: values.Title ?? "",
      AttractionURL: values.AttractionURL ?? "",
      AttractionCategory: values.AttractionCategory ?? "",
      PropertyID: values.PropertyID ?? null,
      TagLine: values.TagLine ?? "",
      Distance: values.Distance ?? "",
      TravelTime: values.TravelTime ?? "",
      BestTime: values.BestTime ?? "",
      Duration: values.Duration ?? "",
      Description: values.Description ?? "",
      Dos: values.Dos ?? "",
      Donts: values.Donts ?? "",
      MetaTitle: values.MetaTitle ?? "",
      MetaDescription: values.MetaDescription ?? "",
      MetaKeywords: values.MetaKeywords ?? "",
      IsActive: values.IsActive === 1,
      IsMustVisit: values?.IsMustVisit ? values.IsMustVisit : false,
      BestTimes: values.BestTimes ?? [],
      HowToReach: values.HowToReach ?? [],
      ThingsToDo: values.ThingsToDo ?? [],
      WhyVisit: values.WhyVisit ?? [],
      Nearby: values.Nearby ?? [],
    };

    const ImageData = new FormData();
    const MainImage =
      values?.MainImage && typeof values?.MainImage !== "string"
        ? getFile(values?.MainImage)
        : "";
    const BannerImage =
      values?.BannerImage && typeof values?.BannerImage !== "string"
        ? getFile(values.BannerImage)
        : "";
    try {
      if (AttractionsID) {
        const editPayload = {
          ...insertPayload,
          ID: AttractionsID,
        };

        const response = await dispatch(updateAttractions(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.MainImage && typeof values?.MainImage !== "string") ||
            (values?.BannerImage && typeof values?.BannerImage !== "string") ||
            values.attachment?.length > 0
          ) {
            const Images = values.attachment?.map((fileItem: any) =>
              fileItem.getRawFile(),
            );

            Images?.map((image: any) => ImageData.append("attachment", image));
            ImageData.append("MainImage", MainImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("AttractionID", AttractionsID);
            const imageResponse = await dispatch(
              AttractionsImageUpload(ImageData),
            );
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/attractions");
            } else {
              navigate("/attractions");
            }
          } else {
            navigate("/attractions");
          }
        }
      } else {
        const response = await dispatch(createAttractions(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            ((values?.MainImage && typeof values?.MainImage !== "string") ||
              (values?.BannerImage &&
                typeof values?.BannerImage !== "string") ||
              values.attachment?.length > 0) &&
            response?.payload?.Data?.ID
          ) {
            const Images = values.attachment?.map((fileItem: any) =>
              fileItem.getRawFile(),
            );

            Images?.map((image: any) => ImageData.append("attachment", image));
            ImageData.append("MainImage", MainImage);
            ImageData.append("BannerImage", BannerImage);
            ImageData.append("AttractionID", response?.payload?.Data?.ID);
            const imageResponse = await dispatch(
              AttractionsImageUpload(ImageData),
            );
            if (imageResponse?.meta?.requestStatus === "fulfilled") {
              navigate("/attractions");
            } else {
              navigate("/attractions");
            }
          } else {
            navigate("/attractions");
          }
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("AttractionURL", { value: slug });
  };

  // if (loading) return <LoadingPanel gridRef={gridRef} />;
  return (
    <>
      {loading && <LoadingPanel gridRef={gridRef} />}
      <div>
        <Form
          onSubmit={handleSubmit}
          key={formKey}
          initialValues={AttractionsDetail}
          render={(formRenderProps: FormRenderProps) => (
            <FormElement>
              <ShadowCard style={{ padding: 12 }}>
                <GridLayout
                  style={{ marginRight: 30 }}
                  gap={{ rows: 5, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <GridLayoutItem colSpan={3}>
                    <Typography.h4>
                      {AttractionsID ? "Update Attraction" : "Add Attraction"}
                    </Typography.h4>
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      display: "flex",
                      justifyContent: "end",
                      alignItems: "flex-start",
                    }}
                  >
                    <ButtonWithLoading
                      label={AttractionsID ? "Update" : "Save"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => navigate("/attractions")}
                    >
                      Cancel
                    </Button>
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="Title"
                      label="Title"
                      placeholder="Title"
                      onChange={(e) => handleCreateSlug(e, formRenderProps)}
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      width: "100%",
                      margin: "18px 0px 0px",
                      padding: "0px 10px 8px",
                      fontWeight: 700,
                      background: "whitesmoke",
                      borderRadius: 5,
                      height: 48,
                    }}
                  >
                    <Field
                      name="IsMustVisit"
                      label="Must Visit Place"
                      component={FormCheckbox}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={3}>
                    <Field
                      name="AttractionURL"
                      label="Attraction URL"
                      placeholder="Attraction URL"
                      component={FormTextField}
                      validator={requiredValidator}
                      astrike={true}
                      onInput={(e: any) => {
                        e.target.value = e?.target?.value
                          ?.toLowerCase()
                          ?.trim(" ")
                          ?.replace(/\s+/g, "-");
                      }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      id={"PropertyID"}
                      name={"PropertyID"}
                      label={"Property Name"}
                      placeholder="Property Name"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={PropertyList?.map((property: IProperty) => {
                        return {
                          value: property?.ID,
                          label: property?.PropertyName,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="TagLine"
                      label="Tag Line"
                      placeholder="Tag Line"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Distance"
                      label="Distance"
                      placeholder="e.g., 45 km"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="TravelTime"
                      label="Travel Time"
                      placeholder="Time"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="BestTime"
                      label="Best Time"
                      placeholder="Time"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name="Duration"
                      label="Duration"
                      placeholder="Duration"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`AttractionCategory`}
                      label="Category"
                      placeholder="Category"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={[
                        {
                          label: "Wildlife",
                          value: "Wildlife",
                        },
                        {
                          label: "Temples",
                          value: "Temples",
                        },
                        {
                          label: "Beaches",
                          value: "Beaches",
                        },
                        {
                          label: "Nature",
                          value: "Nature",
                        },
                        {
                          label: "Culture",
                          value: "Culture",
                        },
                        {
                          label: "One-day trips",
                          value: "One-day trips",
                        },
                      ]}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      name={`IsActive`}
                      label="Status"
                      placeholder="i.e. Active"
                      component={FormSelectionField}
                      validator={requiredValidator}
                      astrike={true}
                      options={STATUSARRAY?.map((status: any) => {
                        return {
                          value: status?.value,
                          label: status?.label,
                        };
                      })}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="Description"
                      label="Description"
                      component={FormRichTextField}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="Dos"
                      label="Do's"
                      component={FormRichTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={2}>
                    <Field
                      name="Donts"
                      label="Don'ts"
                      component={FormRichTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      display: "flex",
                      width: "100%",
                      gap: "10px",
                    }}
                  >
                    <Field
                      justifyContent="start"
                      name={`MainImage`}
                      label="Main Image"
                      type="file"
                      alternateName="Main Image"
                      component={FormImageUpload}
                      validator={requiredValidator}
                      astrike={true}
                    />
                    <Field
                      justifyContent="start"
                      name={`BannerImage`}
                      label="Banner Image"
                      type="file"
                      alternateName="Banner Image"
                      component={FormImageUpload}
                      validator={requiredValidator}
                      astrike={true}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <hr />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4} className="p-2">
                    <FieldArray
                      formRenderProps={formRenderProps}
                      component={WhyVisitArray}
                      name="WhyVisit"
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <hr />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4} className="p-2">
                    <FieldArray
                      formRenderProps={formRenderProps}
                      component={HowToReachArray}
                      name="HowToReach"
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <hr />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4} className="p-2">
                    <FieldArray
                      formRenderProps={formRenderProps}
                      component={BestTimeToVisitArray}
                      name="BestTimes"
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <hr />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4} className="p-2">
                    <FieldArray
                      formRenderProps={formRenderProps}
                      component={ThingsToDoThereArray}
                      name="ThingsToDo"
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <hr />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4} className="p-2">
                    <FieldArray
                      formRenderProps={formRenderProps}
                      component={AttractionNearbyArray}
                      name="Nearby"
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <hr />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4} className="p-2">
                    <FieldArray
                      formRenderProps={formRenderProps}
                      component={PhotoGalleryArray}
                      name="Images"
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <hr />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="MetaTitle"
                      label="Meta Title"
                      placeholder="Meta Title"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="MetaKeywords"
                      label="Keyword"
                      placeholder="Keyword"
                      component={FormTextField}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem colSpan={4}>
                    <Field
                      name="MetaDescription"
                      label="Meta Description"
                      placeholder="Meta Description"
                      component={FormTextArea}
                      style={{ height: 130 }}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem
                    colSpan={4}
                    style={{
                      marginTop: "20px",
                      textAlign: "end",
                    }}
                  >
                    <div>
                      <ButtonWithLoading
                        label={AttractionsID ? "Update" : "Save"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                      <Button
                        type="button"
                        fillMode={"outline"}
                        themeColor={"primary"}
                        style={{ marginLeft: 10 }}
                        onClick={() => navigate("/attractions")}
                      >
                        Cancel
                      </Button>
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </ShadowCard>
            </FormElement>
          )}
        />
      </div>
    </>
  );
};

export default CreateAttractions;
